import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, QrCode, MapPin, Truck, Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import QRCode from "qrcode";
import { useStore, FREE_SHIPPING_THRESHOLD } from "../context/StoreContext";
import { api, formatBRL, formatApiError } from "../api";
import { toast } from "sonner";

const STEPS = ["Entrega", "Frete", "Pagamento", "Pronto"];

function PixQR({ payload }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && payload) {
      QRCode.toCanvas(ref.current, payload, { width: 210, margin: 1, color: { dark: "#0F172A", light: "#ffffff" } });
    }
  }, [payload]);
  return <canvas ref={ref} data-testid="vult-checkout-pix-qr-code" className="rounded-2xl border-4 border-slate-900 bg-white" />;
}

export function CheckoutModal() {
  const { cart, subtotal, discount, coupon, checkoutOpen, setCheckoutOpen, clearCart } = useStore();
  const [step, setStep] = useState(1);
  const [sending, setSending] = useState(false);
  const [order, setOrder] = useState(null);
  const [pixData, setPixData] = useState(null);
  const [shipping, setShipping] = useState("normal");
  const [form, setForm] = useState({ nome: "", email: "", cpf: "", whatsapp: "", cep: "", rua: "", numero: "", cidade: "", estado: "" });

  const freeNormal = subtotal - discount >= FREE_SHIPPING_THRESHOLD;
  const shippingCost = shipping === "express" ? 29.9 : freeNormal ? 0 : 19.9;
  const total = subtotal - discount + shippingCost;

  const close = () => {
    setCheckoutOpen(false);
    setTimeout(() => { setStep(1); setOrder(null); setPixData(null); }, 300);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const confirm = async () => {
    setSending(true);
    try {
      const { data: created } = await api.post("/orders", {
        customer_name: form.nome,
        customer_email: form.email,
        customer_cpf: form.cpf,
        customer_phone: form.whatsapp,
        address: { cep: form.cep, rua: form.rua, numero: form.numero, cidade: form.cidade, estado: form.estado },
        items: cart.map((i) => ({ product_id: i.product_id, name: i.name, brand: i.brand, image: i.image, price: i.price, size: i.size, color: i.color || "", qty: i.qty })),
        shipping_method: shipping,
        shipping_cost: shippingCost,
        payment_method: "pix",
        coupon_code: coupon?.code || "",
        subtotal,
        total,
      });
      const { data: pix } = await api.get(`/config/pix?valor=${total.toFixed(2)}&pedido=${created.id}`);
      setOrder(created);
      setPixData(pix);
      setStep(4);
      clearCart();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Falha ao criar pedido"));
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none transition-colors focus:border-slate-900";

  return (
    <AnimatePresence>
      {checkoutOpen && (
        <motion.div
          data-testid="vult-checkout-modal"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/60 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={close}
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 sm:rounded-3xl sm:p-8"
          >
            <button data-testid="vult-checkout-close-btn" onClick={close} className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 transition-colors hover:bg-slate-900 hover:text-white">
              <X size={18} />
            </button>

            <h3 className="font-display text-3xl font-black uppercase text-slate-900">Checkout</h3>
            <div className="mt-4 flex gap-2">
              {STEPS.map((s, i) => (
                <div key={s} className="flex-1">
                  <div className={`h-1.5 rounded-full ${step > i ? "bg-vult-red" : "bg-slate-200"}`} />
                  <div className={`mt-1 font-mono text-[9px] font-bold uppercase tracking-widest ${step > i ? "text-slate-900" : "text-slate-400"}`}>{s}</div>
                </div>
              ))}
            </div>

            {step === 1 && (
              <form data-testid="vult-checkout-shipping-form" className="mt-6 space-y-3" onSubmit={(e) => { e.preventDefault(); setStep(2); }}>
                <div className="flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-widest text-slate-600"><MapPin size={14} className="text-vult-red" /> Dados &amp; endereço</div>
                <input required data-testid="vult-checkout-name-input" value={form.nome} onChange={set("nome")} placeholder="Nome completo" className={inputCls} />
                <input required type="email" data-testid="vult-checkout-email-input" value={form.email} onChange={set("email")} placeholder="E-mail (recebe a confirmação)" className={inputCls} />
                <div className="grid grid-cols-2 gap-3">
                  <input required data-testid="vult-checkout-cpf-input" value={form.cpf} onChange={set("cpf")} placeholder="CPF" className={inputCls} />
                  <input required data-testid="vult-checkout-whatsapp-input" value={form.whatsapp} onChange={set("whatsapp")} placeholder="WhatsApp (DDD + número)" className={inputCls} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <input required data-testid="vult-checkout-cep-input" value={form.cep} onChange={set("cep")} placeholder="CEP" className={inputCls} />
                  <input required data-testid="vult-checkout-rua-input" value={form.rua} onChange={set("rua")} placeholder="Rua" className={`${inputCls} col-span-2`} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <input required data-testid="vult-checkout-numero-input" value={form.numero} onChange={set("numero")} placeholder="Nº" className={inputCls} />
                  <input required data-testid="vult-checkout-cidade-input" value={form.cidade} onChange={set("cidade")} placeholder="Cidade" className={inputCls} />
                  <input required data-testid="vult-checkout-estado-input" value={form.estado} onChange={set("estado")} placeholder="UF" maxLength={2} className={inputCls} />
                </div>
                <button data-testid="vult-checkout-step1-next" type="submit" className="w-full rounded-full bg-slate-900 py-3.5 font-mono text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-vult-red hover:text-slate-900">
                  Continuar
                </button>
              </form>
            )}

            {step === 2 && (
              <div className="mt-6 space-y-3">
                <div className="flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-widest text-slate-600"><Truck size={14} className="text-vult-red" /> Método de frete</div>
                {[
                  { id: "normal", label: "Entrega normal", time: "Prazo estimado: 20 dias até o destino final", price: freeNormal ? 0 : 19.9 },
                  { id: "express", label: "Express Vult", time: "15 a 20 dias • envio prioritário", price: 29.9 },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    data-testid={`vult-checkout-shipping-${opt.id}`}
                    onClick={() => setShipping(opt.id)}
                    className={`flex w-full items-center justify-between rounded-2xl border-2 p-4 text-left transition-all ${shipping === opt.id ? "border-slate-900 bg-[#F8F9FA] shadow-[3px_3px_0_#FFD600]" : "border-slate-200"}`}
                  >
                    <div>
                      <div className="font-display text-lg font-bold uppercase text-slate-900">{opt.label}</div>
                      <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">{opt.time}</div>
                    </div>
                    <span className={`font-mono text-sm font-bold ${opt.price === 0 ? "text-emerald-600" : "text-slate-900"}`}>
                      {opt.price === 0 ? "Grátis" : formatBRL(opt.price)}
                    </span>
                  </button>
                ))}
                <div className="flex gap-2 pt-2">
                  <button data-testid="vult-checkout-step2-back" onClick={() => setStep(1)} className="flex-1 rounded-full border border-slate-300 py-3.5 font-mono text-sm font-bold uppercase tracking-widest transition-colors hover:border-slate-900">Voltar</button>
                  <button data-testid="vult-checkout-step2-next" onClick={() => setStep(3)} className="flex-1 rounded-full bg-slate-900 py-3.5 font-mono text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-vult-red hover:text-slate-900">Pagamento</button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="vult-checkout-payment-form" className="mt-6 space-y-4">
                <div className="space-y-3 rounded-2xl bg-[#F8F9FA] p-5 text-center">
                  <QrCode size={32} className="mx-auto text-vult-blue" />
                  <p className="font-display text-xl font-black uppercase text-slate-900">PIX direto Vult</p>
                  <p className="text-sm text-slate-600">Ao confirmar, geramos o QR Code com o valor exato do pedido. O valor cai direto na conta da loja, sem intermediários.</p>
                </div>

                <div className="rounded-2xl border border-slate-200 p-4 text-sm">
                  <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{formatBRL(subtotal)}</span></div>
                  {discount > 0 && <div className="flex justify-between font-bold text-emerald-600"><span>Cupom</span><span>-{formatBRL(discount)}</span></div>}
                  <div className="flex justify-between text-slate-600"><span>Frete</span><span>{shippingCost === 0 ? "Grátis" : formatBRL(shippingCost)}</span></div>
                  <div className="mt-2 flex justify-between border-t border-slate-200 pt-2 font-display text-xl font-black text-slate-900">
                    <span>Total</span><span data-testid="vult-checkout-total">{formatBRL(total)}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button data-testid="vult-checkout-step3-back" onClick={() => setStep(2)} className="flex-1 rounded-full border border-slate-300 py-3.5 font-mono text-sm font-bold uppercase tracking-widest transition-colors hover:border-slate-900">Voltar</button>
                  <button
                    data-testid="vult-checkout-submit-btn"
                    onClick={confirm}
                    disabled={sending}
                    className="flex flex-[2] items-center justify-center gap-2 rounded-full bg-vult-red py-3.5 font-mono text-sm font-bold uppercase tracking-widest text-slate-900 transition-colors hover:bg-slate-900 hover:text-white disabled:opacity-50"
                  >
                    {sending ? <Loader2 size={16} className="animate-spin" /> : null}
                    {sending ? "Gerando..." : `Gerar PIX ${formatBRL(total)}`}
                  </button>
                </div>
              </div>
            )}

            {step === 4 && order && pixData && (
              <div data-testid="vult-checkout-success-view" className="mt-6 flex flex-col items-center text-center">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 12 }}>
                  <CheckCircle2 size={56} className="text-emerald-500" />
                </motion.div>
                <h4 className="mt-3 font-display text-2xl font-black uppercase text-slate-900">Pedido {order.id} criado!</h4>
                <p className="mt-1 text-sm text-slate-600">Escaneie o QR Code ou copie o código para pagar:</p>

                <div data-testid="vult-checkout-pix-receiver" className="mt-3 flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2">
                  <ShieldCheck size={14} className="shrink-0 text-emerald-600" />
                  <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                    Você está pagando para {pixData.nome}
                  </span>
                </div>

                <div className="mt-4"><PixQR payload={pixData.payload} /></div>

                <div className="mt-3 w-full truncate rounded-xl border border-slate-200 bg-[#F8F9FA] px-3 py-2 font-mono text-[10px] text-slate-500">{pixData.payload}</div>
                <button
                  data-testid="vult-checkout-pix-copy-btn"
                  type="button"
                  onClick={() => { navigator.clipboard?.writeText(pixData.payload); toast.success("Código PIX copiado"); }}
                  className="mt-2 flex items-center gap-2 rounded-full border border-slate-900 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-widest transition-colors hover:bg-slate-900 hover:text-white"
                >
                  <Copy size={13} /> PIX copia e cola
                </button>

                <div className="mt-5 w-full space-y-2 rounded-2xl bg-[#F8F9FA] p-5 text-left font-mono text-xs">
                  <div className="flex justify-between"><span className="text-slate-500">Valor</span><span className="font-bold">{formatBRL(order.total)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Rastreio</span><span data-testid="vult-checkout-tracking" className="font-bold text-vult-blue">{order.tracking_code}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Chave PIX</span><span className="font-bold">{pixData.key}</span></div>
                </div>

                <p data-testid="vult-checkout-prazo" className="mt-4 rounded-full bg-vult-lime px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-900">
                  Prazo estimado: 20 dias até o destino final
                </p>
                <p className="mt-3 text-xs text-slate-500">Assim que o pagamento for identificado, você recebe a confirmação por e-mail.</p>

                <a
                  data-testid="vult-checkout-track-order-btn"
                  href={`/pedido/${order.id}`}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border-2 border-slate-900 py-3.5 font-mono text-sm font-bold uppercase tracking-widest text-slate-900 transition-colors hover:bg-vult-lime"
                >
                  <Truck size={15} /> Acompanhar meu pedido
                </a>

                <button data-testid="vult-checkout-success-close" onClick={close} className="mt-3 w-full rounded-full bg-slate-900 py-3.5 font-mono text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-vult-red hover:text-slate-900">
                  Continuar comprando
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
